import React, { useState, useEffect, useMemo } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
    <span
        className={`placeholder placeholder-wave col-12 ${className}`}
        style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
    />
);

const HODDashboard = () => {
    const baseUrl = API_BASE_URL;
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [reportingSummary, setReportingSummary] = useState({ reported_goals: 0, not_reported: 0 });

    // Fetch profile first to get department_id / department
    useEffect(() => {
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me`, {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                });
                setProfile(response.data);
            } catch (err) {
                console.error('Error fetching user profile:', err);
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };
        fetchProfile();
    }, [baseUrl]);

    // Fetch analytics for department once profile is loaded
    useEffect(() => {
        const fetchAnalytics = async () => {
            const departmentId = profile?.employee?.department;
            if (!departmentId) return;
            setLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params: { department: departmentId }
                });
                setAnalytics(response.data);
            } catch (err) {
                console.error('Error fetching analytics:', err);
                setAnalytics(null);
            } finally {
                setLoading(false);
            }
        };
        if (profile && !profileLoading) {
            fetchAnalytics();
        }
    }, [profile, profileLoading, baseUrl]);

    // Fetch reporting summary after analytics loads
    useEffect(() => {
        const fetchReportingSummary = async () => {
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/reporting/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                setReportingSummary(response.data);
            } catch (err) {
                console.error('Error fetching reporting summary:', err);
                setReportingSummary({ reported_goals: 0, not_reported: 0 });
            }
        };
        if (analytics) {
            fetchReportingSummary();
        }
    }, [analytics, baseUrl]);

    const stats = analytics?.overall_statistics || {};

    // Transform quarterly_trends object to array for recharts
    const quarterlyTrendsData = useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, dataStats]) => ({
            quarter,
            ...dataStats
        }));
    }, [analytics]);

    // Prepare data for reporting summary pie chart
    const reportingPieData = [
        { name: 'Reported', value: reportingSummary.reported_goals || 0 },
        { name: 'Not Reported', value: reportingSummary.not_reported || 0 }
    ];
    const reportingPieColors = ['#4F46E5', '#ef4444'];

    const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 18;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12} fontWeight={600}>
                {`${name}: ${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    return (
        <div
            data-sidebar-skin="dark"
            data-header-skin="light"
            data-navbar-brand-skin="dark"
            data-sidebar-state="expand"
        >
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">
                    {/* Page Header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Performance Dashboard</h4>
                            <p className="text-muted mb-0 fs-13">Overview of employee performance metrics and KPIs</p>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="row g-3 mb-4">
                        {/* Total Employees */}
                        <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted fs-13 mb-1">Total Employees</p>
                                                <h3 className="fw-bold mb-0 text-dark">
                                                    {stats.total_employees !== undefined ? stats.total_employees : 0}
                                                </h3>
                                            </div>
                                            <div className="avatar avatar-lg bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center text-primary">
                                                <i className="ti ti-users fs-24"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Average Performance */}
                        <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted fs-13 mb-1">Average Performance</p>
                                                <h3 className="fw-bold mb-0 text-dark">
                                                    {stats.average_progress !== undefined ? `${stats.average_progress}%` : '0%'}
                                                </h3>
                                            </div>
                                            <div className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center text-success">
                                                <i className="ti ti-trending-up fs-24"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Reporting Rate */}
                        <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted fs-13 mb-1">Reporting Rate</p>
                                                <h3 className="fw-bold mb-0 text-dark">
                                                    {reportingSummary.reporting_rate !== undefined && reportingSummary.reporting_rate !== null
                                                        ? `${Number(reportingSummary.reporting_rate).toFixed(2)}%`
                                                        : '0.00%'}
                                                </h3>
                                            </div>
                                            <div className="avatar avatar-lg bg-info-transparent rounded-circle d-flex align-items-center justify-content-center text-info">
                                                <i className="ti ti-chart-donut-3 fs-24"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Completed KPIs */}
                        <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted fs-13 mb-1">Completed KPIs</p>
                                                <h3 className="fw-bold mb-0 text-dark">
                                                    {stats.completed_goals !== undefined ? `${stats.completed_goals} / ${stats.total_goals || 0}` : '0 / 0'}
                                                </h3>
                                            </div>
                                            <div className="avatar avatar-lg bg-warning-transparent rounded-circle d-flex align-items-center justify-content-center text-warning">
                                                <i className="ti ti-target fs-24"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Grid */}
                    <div className="row g-4 mb-4">
                        {/* Quarterly Performance Trends Chart */}
                        <div className="col-lg-8">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                                    <h6 className="card-title mb-0 fw-semibold text-dark">Quarterly Performance Trends</h6>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="placeholder-glow py-4">
                                            <Shimmer width="100%" height="280px" rounded="8px" />
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                                        <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="quarter" />
                                                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                                <Tooltip formatter={(value) => `${value}%`} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="completion_rate"
                                                    stroke="#4F46E5"
                                                    fillOpacity={1}
                                                    fill="url(#colorQuarterly)"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Reporting Summary Pie Chart */}
                        <div className="col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                                    <h6 className="card-title mb-0 fw-semibold text-dark">Reporting Summary</h6>
                                </div>
                                <div className="card-body d-flex align-items-center justify-content-center">
                                    {loading ? (
                                        <div className="placeholder-glow py-4 w-100">
                                            <Shimmer width="100%" height="240px" rounded="8px" />
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={260}>
                                            <PieChart>
                                                <Pie
                                                    data={reportingPieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={55}
                                                    outerRadius={75}
                                                    fill="#8884d8"
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    labelLine={true}
                                                    label={renderPieLabel}
                                                >
                                                    {reportingPieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={reportingPieColors[index % reportingPieColors.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    );
};

export default HODDashboard;